import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query/react'

import appConfig from '~/config/app'
import BadgeCard from '~/statistics-v2/components/BadgeCard'
import MindTipsCard from '~/statistics-v2/components/MindTipsCard'
import TrackerCard from '~/statistics-v2/components/TrackerCard'
import StatisticProgressBar from '~/statistics-v2/components/StatsProgressBar'
import { useGetMappingQuizPrerequisiteTasksQuery } from '~/curriculum/apis/mapping-quiz.api'
import { useGetCurrentGradeQuery } from '~/curriculum/apis/curriculum.api'
import { useGetUserSubtopicTimesQuery, useGetUserTimeQuery } from '~/statistics-v2/api/statistics.api'
import {
  useGetStudentCurrentGradeQuery,
  useGetStudentQuizTaskQuery,
  useGetStudentSubtopicTimesQuery,
  useGetStudentTimeQuery,
} from '~/teacher-panel/apis/studentStats.api'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import ArrowLeftBoldIcon from '~/components/Icons/svgs/ArrowLeftBoldIcon'
import { TopicStats } from '~/statistics-v2/types/statistics.types'
import { calculateProgress, formatTimeSpent, getOverallGrade } from '~/statistics-v2/utils/statistics.utils'
import SubTopicWrapper from '~/statistics-v2/components/SubTopicWrapper'
import { TopicId } from '~/dashboard/utils/learningPath.utils'
import { AppLanguage } from '~/types/locale'
import ActivityIcon from '~/components/Icons/svgs/ActivityIcon'
import AimIcon from '~/components/Icons/svgs/AimIcon'
import ClockSemiboldIcon from '~/components/Icons/svgs/ClockSemiboldIcon'
import { slugify } from '~/shared/sanity/utils/curriculum.utils'

import { getSubtopicMustKnowsProgress } from '../helper/statistics.helper'

export default function TopicDetailStats({
  onBackClick,
  topicStats,
  selectedGrade,
  countryCode,
  isSmallScreen,
  userId,
}: {
  onBackClick: () => void
  topicStats: TopicStats
  selectedGrade: { key: string; label: string }
  countryCode: string
  isSmallScreen: boolean
  /** Whose statistics these are, if not the signed-in user's: a family member in b2c, a pupil in b2s. */
  userId?: number
}): React.ReactElement {
  const gradeId = `${countryCode}-grade-${selectedGrade.key}`
  const [timeSpent, setTimeSpent] = useState(topicStats.timeSpent)
  const [videoWatchTime, setVideoWatchTime] = useState(topicStats.videoWatchTime)
  const [isMindTipsVisible, setIsMindTipsVisible] = useState(true)

  /**
   * ⚠ **The edition picks the endpoint, not the prop.** `/progress/get-user-time` drops its `userId`
   * and answers about the token holder, so on the teacher's screen it returned 200 with the TEACHER's
   * minutes under the child's name — a substitution, not an error, so nothing looked wrong.
   *
   * `userId !== undefined` alone is the wrong test: `/family/admin/stats` sets it to the family member
   * being viewed, and gating on the prop alone points that b2c page at a school URL.
   */
  const isStudentView = appConfig.isSchoolPlatform && userId !== undefined
  const hasTopic = topicStats.order !== undefined

  const selfTime = useGetUserTimeQuery(
    { countryCode, gradeId, topicOrder: topicStats.order, userId },
    { skip: !hasTopic || isStudentView },
  )

  // `skipToken`, not `skip`: `studentId` is required and there is no honest value for it in b2c.
  const studentTime = useGetStudentTimeQuery(
    isStudentView && hasTopic ? { studentId: userId, countryCode, gradeId, topicOrder: topicStats.order } : skipToken,
  )

  const { data: topicTime, isLoading: isTimeLoading } = isStudentView ? studentTime : selfTime

  /*
   * Every subtopic's time in ONE request, not one per subtopic. hom-api #1935 moved time out of
   * `get-user-stats` because computing it per node made the panel slow; asking per expanded subtopic
   * here would recreate that shape from the other side. The edition picks the endpoint for the same
   * reason it does above — the b2c route answers about the token holder unless `userId` passes its
   * family-admin check, so on the teacher's screen it would return the TEACHER's minutes.
   */
  const selfSubtopicTimes = useGetUserSubtopicTimesQuery({ userId }, { skip: isStudentView })
  const studentSubtopicTimes = useGetStudentSubtopicTimesQuery(isStudentView ? { studentId: userId } : skipToken)
  const subtopicTimes = (isStudentView ? studentSubtopicTimes : selfSubtopicTimes).data?.subtopics

  // Same split as time above: `/curriculum/quiz-task` reads the token holder, so on the teacher's
  // screen it answered with the TEACHER's (empty) tasks and the cards listed no Must-Knows.
  const selfQuizTask = useGetMappingQuizPrerequisiteTasksQuery(
    { countryCode, gradeLevel: selectedGrade.key, subjectOrder: topicStats.order ?? 0, version: 'v2' },
    { skip: !hasTopic || isStudentView },
  )
  const studentQuizTaskArgs =
    isStudentView && hasTopic
      ? { studentId: userId, countryCode, gradeLevel: selectedGrade.key, subjectOrder: topicStats.order ?? 0 }
      : skipToken
  const studentQuizTask = useGetStudentQuizTaskQuery(studentQuizTaskArgs)
  const topicMustKnowTasks = (isStudentView ? studentQuizTask : selfQuizTask).data?.tasks ?? {}

  // Self-only too, so the pupil's position comes from the school twin: it picks which subtopic opens.
  const selfCurrentGrade = useGetCurrentGradeQuery(undefined, { skip: userId !== undefined })
  const studentCurrentGrade = useGetStudentCurrentGradeQuery(isStudentView ? { studentId: userId } : skipToken)
  const { data: currentGrade, isLoading: isCurrentGradeLoading } = isStudentView
    ? studentCurrentGrade
    : selfCurrentGrade
  const subtopicEntries = Object.entries(topicStats.subtopics ?? {})
  const isCurrentGrade = currentGrade?.grade != null && Number(selectedGrade.key) === currentGrade.grade
  const isCurrentTopic =
    isCurrentGrade && topicStats.order !== undefined && topicStats.order === currentGrade?.subjectOrder
  const currentSubtopicIndex =
    isCurrentTopic && currentGrade?.subTopicOrder != null
      ? subtopicEntries.findIndex(([, subTopic]) => subTopic.subTopicLevlNo === currentGrade.subTopicOrder)
      : -1
  // Hold off defaulting to the first subtopic until the current-grade query resolves. Otherwise we expand
  // subtopic 0, then collapse it once the real current-subtopic index arrives — and that collapse shrinks the
  // content above the target card mid-scroll, so the smooth scrollIntoView overshoots and leaves the card's
  // top above the viewport. While loading we expand nothing (-1) and stay at the top.
  const resolvedDefaultIndex = isCurrentGradeLoading ? -1 : 0
  const expandedIndex = currentSubtopicIndex >= 0 ? currentSubtopicIndex : resolvedDefaultIndex
  const expandedSubtopicRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (topicTime) {
      setTimeSpent(topicTime.timeSpent ?? topicStats.timeSpent)
      setVideoWatchTime(topicTime.videoWatchTime ?? topicStats.videoWatchTime)
    }
  }, [topicStats.timeSpent, topicStats.videoWatchTime, topicTime, isTimeLoading])

  const { t } = useTranslation('statisticsV2')
  const router = useRouter()
  const totalQuestions = (topicStats.totalQuestionsAttempted || 0) + (topicStats.totalMustKnowQuestionsAttempted || 0)

  const accuracy = (topicStats.correctQuestions / topicStats.totalQuestions) * 100 || 0

  const grade = getOverallGrade(accuracy, router.locale as AppLanguage)
  const subTopicProgress = calculateProgress(topicStats.completedSubtopics, topicStats.totalSubtopics)

  useEffect(() => {
    if (isCurrentGradeLoading || !currentGrade) return
    if (expandedIndex > 0 && expandedSubtopicRef.current) {
      const timer = setTimeout(() => {
        const rect = expandedSubtopicRef.current?.getBoundingClientRect()
        if (rect) {
          const top = Math.max(0, rect.top + window.scrollY - 80)
          window.scrollTo({ top, behavior: 'smooth' })
        }
      }, 1300)
      return () => clearTimeout(timer)
    } else {
      window.scrollTo(0, 0)
    }
  }, [expandedIndex, isCurrentGradeLoading, currentGrade])

  return (
    <div className="flex relative flex-col justify-start items-start w-87.5 lg:w-230 gap-4">
      <button className="inline-flex items-center gap-2 text-white absolute -top-6 lg:-left-36" onClick={onBackClick}>
        <SVGIcon icon={ArrowLeftBoldIcon} pathClassName="text-white" />
      </button>
      <div className="flex lg:flex-row flex-col gap-4 w-full justify-between">
        <div className="flex items-start lg:items-center gap-4 h-23 mb-8">
          <div className={!topicStats.isCompleted ? 'grayscale' : ''}>
            <ResponsiveImage
              alt="Certificate"
              fit="contain"
              height={1080}
              imageClassName="w-24"
              srcKey={`curriculum/certificate/mastery-certificate-${topicStats.topicSlug ?? TopicId.NUMBERS}_v4.png`}
              width={1080}
            />
          </div>
          <div className="flex flex-col px-2 gap-3">
            <h2 className="text-white text-xl lg:text-3xl font-bold">
              {selectedGrade.label} : {topicStats.name}
            </h2>
            <p className="text-purple-pastel text-sm font-semibold">{t('deepDive', { topicName: topicStats.name })}</p>
          </div>
        </div>
        <div>
          <BadgeCard
            fullWidthInSmallScreen
            srcKey="icons/products/test_prep_badge.png"
            subTitle={topicStats.name ?? ''}
            title={t('currently')}
            value={grade}
          />
        </div>
      </div>
      {isMindTipsVisible && (
        <div className="w-full">
          <MindTipsCard percentage={accuracy} setIsMindTipsCardVisible={setIsMindTipsVisible} />
        </div>
      )}
      <div className=" border w-full border-white/20 rounded-4 p-4 flex flex-col gap-1">
        <StatisticProgressBar
          barHeight="regular"
          barType="straight"
          FirstSubtitle={`${topicStats.completedSubtopics} / ${topicStats.totalSubtopics} Sub-topics`}
          progressPercentage={subTopicProgress}
          SecondSubtitle=" "
        />
      </div>
      <div className="flex flex-wrap gap-4 justify-between w-full">
        <TrackerCard
          icon={ClockSemiboldIcon}
          isLarge
          isLoading={isTimeLoading}
          title={t('statisticsV2:timeSpent')}
          value={formatTimeSpent(timeSpent)}
        />
        <TrackerCard
          icon={ClockSemiboldIcon}
          isLarge
          isLoading={isTimeLoading}
          title={t('statisticsV2:videoWatchTime')}
          value={videoWatchTime > 0 ? formatTimeSpent(videoWatchTime) : null}
        />
        <TrackerCard
          icon={AimIcon}
          isLarge
          title={t('statisticsV2:accuracy')}
          value={accuracy > 0 ? `${Math.round(accuracy)}%` : null}
        />
        <TrackerCard icon={ActivityIcon} isLarge title={t('statisticsV2:activities')} value={totalQuestions} />
      </div>

      {subtopicEntries.map(([subTopicId, subTopic], index) => {
        const subtopicMustKnows = subTopic.name ? topicMustKnowTasks[slugify(subTopic.name)] : []

        const mustKnowsProgress = subtopicMustKnows
          ? getSubtopicMustKnowsProgress(subTopic.mustKnowTopicsProgress, subtopicMustKnows)
          : []
        return (
          <div
            className="flex items-center gap-4 w-full h-full"
            key={subTopicId}
            ref={index === expandedIndex ? expandedSubtopicRef : undefined}
          >
            <SubTopicWrapper
              countryCode={countryCode}
              isSmallScreen={isSmallScreen}
              mustKnowsProgress={mustKnowsProgress}
              shouldExpand={index === expandedIndex}
              subTopic={subTopic}
              subtopicTimes={subtopicTimes}
            />
          </div>
        )
      })}
    </div>
  )
}
